import { GeckoFeature } from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { compassOrientationFormField } from 'types/assetModel/commonFields'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import { GeckoVerticalWeldJSON } from 'types/assetModel/features/welds/geckoVerticalWeld'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { forecastMath } from 'utils/fcMath'
import { parseNumber } from 'utils/parsingHelpers'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

const { pi } = forecastMath

export type LaunchTubeAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.LaunchTube
  height: number
  radius: number
  scene_config: Record<string, unknown>
  component_bounds: Record<string, number>
  features: Record<string, GeckoVerticalWeldJSON>
  compass_orientation: number | undefined
}

export const LaunchTubeAvailableFeatures: GeckoFeatureModelType[] = []

export const NewLaunchTubeAssetModelJSON: LaunchTubeAssetModelJSON = {
  name: 'New Launch Tube',
  type: GeckoComponentModelType.LaunchTube,
  height: 0,
  radius: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  compass_orientation: undefined,
}

export class GeckoLaunchTube extends GeckoEditableComponent {
  type: GeckoComponentModelType.LaunchTube
  features: Map<string, GeckoFeature>
  radius: GeckoMeasurement
  height: GeckoMeasurement
  componentBounds: GeckoComponentRect
  sceneConfig = undefined
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    height: GeckoMeasurement,
    componentBounds: GeckoComponentRect,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.LaunchTube
    this.radius = radius
    this.height = height
    this.componentBounds = componentBounds
    this.compassOrientation = compassOrientation

    // Features
    this.features = new Map()
  }

  public static CreateFromJSON(id: string, json: LaunchTubeAssetModelJSON) {
    const name = json.name
    const radius = GeckoMeasurement.CreateFromValue(
      json.radius,
      GeckoMeasurementUnit.Meters
    )
    const height = GeckoMeasurement.CreateFromValue(
      json.height,
      GeckoMeasurementUnit.Meters
    )

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const compassOrientation = parseNumber(json.compass_orientation)
    return new GeckoLaunchTube(
      id,
      json,
      name,
      radius,
      height,
      componentBounds,
      compassOrientation
    )
  }

  public asJSON(): LaunchTubeAssetModelJSON {
    return generateLaunchTube(
      this.name,
      this.height,
      this.radius,
      this.compassOrientation,
      this.json as LaunchTubeAssetModelJSON
    )
  }
}

const generateLaunchTube = (
  name: string,
  height: GeckoMeasurement,
  radius: GeckoMeasurement,
  compassOrientation: number | undefined,
  otherData: LaunchTubeAssetModelJSON = NewLaunchTubeAssetModelJSON
): LaunchTubeAssetModelJSON => {
  const tubeHeight = height
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tubeRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tubeCircumference = Number(
    (2 * pi * tubeRadius).toPrecision(assetModelPrecision)
  )

  return {
    name: name,
    type: GeckoComponentModelType.LaunchTube,
    height: tubeHeight,
    radius: tubeRadius,
    scene_config: otherData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: tubeCircumference,
      y_end: tubeHeight,
    },
    features: otherData.features,
    compass_orientation: compassOrientation,
  }
}

export const LaunchTubeAssetModelFormFields = (
  JSON: LaunchTubeAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Launch Tube Name',
      value: JSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'height',
      label: 'Height',
      value: JSON.height,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },

    {
      dataKey: 'radius',
      label: 'Diameter',
      value: JSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    compassOrientationFormField(JSON.compass_orientation),
  ]
}

export const LaunchTubeJSONFromFormFields = (
  component: LaunchTubeAssetModelJSON,
  formFields: FormField[]
) => {
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  return generateLaunchTube(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'height')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2,
      GeckoMeasurementUnit.Meters
    ),
    compassOrientation,
    component
  )
}
